import { z } from "zod";

export const PlanSchema = z.enum(["free", "starter", "pro", "studio"]);

export const PlatformSchema = z.enum([
  "twitter",
  "bluesky",
  "linkedin",
  "newsletter",
  "short_video",
]);

export const VoiceMoodSchema = z.enum([
  "default",
  "excited",
  "serious",
  "playful",
  "contemplative",
]);

export const VariantLengthSchema = z.enum(["short", "medium", "long"]);
export const VariantToneSchema = z.enum([
  "professional",
  "neutral",
  "casual",
]);

export const VariantModelSchema = z.enum([
  "auto",
  "gpt-4o-mini",
  "gpt-4o",
  "gpt-4.1",
  "gpt-4.1-mini",
  "gpt-4.1-nano",
  "gpt-5",
  "gpt-5-mini",
  "gpt-5-nano",
  "o3",
  "o3-mini",
  "o4-mini",
  "workers-ai-llama-3.3-70b",
]);

export const RegenerateVariantSchema = z.object({
  length: VariantLengthSchema.optional(),
  tone: VariantToneSchema.optional(),
  instruction: z.string().max(500).optional(),
  mode: z.enum(["append", "replace"]).default("append"),
  model: VariantModelSchema.optional(),
});

export const MagicLinkRequestSchema = z.object({
  email: z.string().email().max(254),
});

export const SignupSchema = z.object({
  email: z.string().email().max(254),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .max(200, "Password too long"),
});

export const LoginSchema = z.object({
  email: z.string().email().max(254),
  password: z.string().min(1).max(200),
});

export const ForgotPasswordSchema = z.object({
  email: z.string().email().max(254),
});

export const ResetPasswordSchema = z.object({
  token: z.string().min(20).max(200),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .max(200, "Password too long"),
});

export const CreateProjectSchema = z.object({
  title: z.string().max(200).optional(),
  input_text: z.string().min(50).max(20000),
  voice_profile_id: z.string().ulid().optional(),
  mood: VoiceMoodSchema.optional(),
});

const VoiceSourceItemSchema = z.object({
  content: z.string().min(50).max(100000),
  source_title: z.string().max(200).optional(),
});

export const CreateVoiceProfileSchema = z.object({
  name: z.string().min(1).max(80),
  sources: z.array(VoiceSourceItemSchema).max(50).optional(),
});

export const AddVoiceSourceSchema = VoiceSourceItemSchema;

export const ImportVoiceSourceUrlSchema = z.object({
  url: z.string().url().max(1000),
  max_items: z.number().int().min(1).max(10).optional(),
});

export const UpdateVoiceProfileSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  profile_json: z
    .string()
    .max(20000)
    .optional()
    .refine(
      (v) => {
        if (v === undefined) return true;
        try {
          JSON.parse(v);
          return true;
        } catch {
          return false;
        }
      },
      { message: "profile_json must be valid JSON" }
    ),
});

export const CheckoutRequestSchema = z.object({
  plan: PlanSchema.exclude(["free"]),
});

export const UpdateVariantSchema = z.object({
  content_edited: z.string().max(50000),
});

export const SocialPlatformSchema = z.enum([
  "bluesky",
  "twitter",
  "linkedin",
  "threads",
]);

export const ConnectBlueskySchema = z.object({
  identifier: z.string().min(3).max(253),
  app_password: z.string().min(1).max(100),
});

export const ConnectTwitterSchema = z.object({
  consumer_key: z.string().min(10).max(200),
  consumer_secret: z.string().min(10).max(200),
  access_token: z.string().min(10).max(200),
  access_token_secret: z.string().min(10).max(200),
});

export const ConnectLinkedInStartSchema = z.object({
  client_id: z.string().min(8).max(200),
  client_secret: z.string().min(8).max(200),
});

export const CreateAutoPlugSchema = z.object({
  name: z.string().min(1).max(80),
  link_url: z.string().url().max(500).optional().nullable(),
  intent: z.string().min(8).max(500),
  delay_hours: z.number().int().min(1).max(72),
  voice_profile_id: z.string().min(1).max(40).optional().nullable(),
  platforms: z.array(SocialPlatformSchema).min(1).max(4),
  ai_model: z.string().min(1).max(80).optional().nullable(),
});

export const UpdateAutoPlugSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  link_url: z.string().url().max(500).optional().nullable(),
  intent: z.string().min(8).max(500).optional(),
  delay_hours: z.number().int().min(1).max(72).optional(),
  voice_profile_id: z.string().min(1).max(40).optional().nullable(),
  platforms: z.array(SocialPlatformSchema).min(1).max(4).optional(),
  status: z.enum(["active", "paused"]).optional(),
  ai_model: z.string().min(1).max(80).optional().nullable(),
});

export const CreateEvergreenSchema = z.object({
  name: z.string().min(1).max(120),
  social_account_id: z.string().min(1).max(40),
  voice_profile_id: z.string().min(1).max(40).optional().nullable(),
  original_content: z.string().min(10).max(8000),
  cadence_days: z.number().int().min(3).max(365),
  max_repeats: z.number().int().min(1).max(50).optional().nullable(),
  ai_model: z.string().min(1).max(80).optional().nullable(),
});

export const UpdateEvergreenSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  voice_profile_id: z.string().min(1).max(40).optional().nullable(),
  original_content: z.string().min(10).max(8000).optional(),
  cadence_days: z.number().int().min(3).max(365).optional(),
  max_repeats: z.number().int().min(1).max(50).optional().nullable(),
  ai_model: z.string().min(1).max(80).optional().nullable(),
  status: z.enum(["active", "paused"]).optional(),
});

export const PreviewEvergreenSchema = z.object({
  voice_profile_id: z.string().min(1).max(40).optional().nullable(),
  original_content: z.string().min(10).max(8000),
  platform: z.enum(["twitter", "bluesky", "linkedin", "threads"]),
  iteration: z.number().int().min(0).max(50).optional(),
  ai_model: z.string().min(1).max(80).optional().nullable(),
});

export const VisualTemplateSchema = z.enum([
  "square",
  "story",
  "tweet_hero",
  "linkedin_carousel",
]);
export type VisualTemplate = z.infer<typeof VisualTemplateSchema>;

export const CreateVisualSchema = z.object({
  template: VisualTemplateSchema,
  text: z.string().min(1).max(4000),
  attribution: z.string().max(80).optional().nullable(),
  palette_dark: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, "Must be a hex color like #1a2b3c")
    .optional(),
  palette_light: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, "Must be a hex color like #1a2b3c")
    .optional(),
  brand: z.string().max(60).optional(),
  slides: z.array(z.string().min(1).max(500)).max(8).optional().nullable(),
});

export const CreateScheduledPostSchema = z.object({
  social_account_id: z.string().min(1).max(50),
  content: z.string().min(1).max(50000),
  scheduled_for: z.number().int().positive(),
  variant_id: z.string().min(1).max(50).optional(),
  project_id: z.string().min(1).max(50).optional(),
  platform: z.string().min(1).max(40).optional(),
  media_asset_ids: z.array(z.string().min(1).max(50)).max(4).optional(),
  first_comment: z.string().max(1250).optional(),
});

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

export const CreateCategorySchema = z.object({
  name: z.string().min(1).max(40),
  color: z.string().regex(HEX_COLOR).optional(),
});

export const UpdateCategorySchema = z.object({
  name: z.string().min(1).max(40).optional(),
  color: z.string().regex(HEX_COLOR).optional(),
  position: z.number().int().min(0).max(999).optional(),
});

export const CreateSlotSchema = z.object({
  social_account_id: z.string().min(1).max(50),
  category_id: z.string().min(1).max(50).optional().nullable(),
  day_of_week: z.number().int().min(0).max(6),
  hour: z.number().int().min(0).max(23),
  minute: z.number().int().min(0).max(59),
  timezone: z.string().min(1).max(50).optional(),
});

export const UpdateSlotSchema = z.object({
  category_id: z.string().min(1).max(50).optional().nullable(),
  day_of_week: z.number().int().min(0).max(6).optional(),
  hour: z.number().int().min(0).max(23).optional(),
  minute: z.number().int().min(0).max(59).optional(),
  timezone: z.string().min(1).max(50).optional(),
});

export const QueueAddSchema = z.object({
  social_account_id: z.string().min(1).max(50),
  content: z.string().min(1).max(50000),
  category_id: z.string().min(1).max(50).optional().nullable(),
  platform: z.string().min(1).max(40).optional(),
});

export const BulkCsvRowSchema = z.object({
  content: z.string().min(1).max(50000),
  category_id: z.string().min(1).max(50).optional().nullable(),
});

export const BulkCsvSchema = z.object({
  social_account_id: z.string().min(1).max(50),
  default_category_id: z.string().min(1).max(50).optional().nullable(),
  rows: z.array(BulkCsvRowSchema).min(1).max(200),
});

export const CreateAutoRetweetSchema = z.object({
  name: z.string().min(1).max(80),
  social_account_id: z.string().min(1).max(50),
  min_likes: z.number().int().min(0).max(1_000_000).optional(),
  min_age_days: z.number().int().min(0).max(3650).optional(),
  max_age_days: z.number().int().min(1).max(3650).optional(),
  cadence_days: z.number().int().min(1).max(90).optional(),
});

export const BulkDeleteScheduledPostsSchema = z.object({
  ids: z.array(z.string().min(1).max(50)).min(1).max(500),
});

export const UpdateAutoRetweetSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  status: z.enum(["active", "paused"]).optional(),
  min_likes: z.number().int().min(0).max(1_000_000).optional(),
  min_age_days: z.number().int().min(0).max(3650).optional(),
  max_age_days: z.number().int().min(1).max(3650).optional(),
  cadence_days: z.number().int().min(1).max(90).optional(),
});

export const HookClusterSchema = z.enum([
  "contrarian",
  "listicle",
  "story",
  "question",
  "stat",
  "declarative",
  "promise",
]);

export const CreateHookSchema = z.object({
  title: z.string().min(1).max(100),
  template: z.string().min(3).max(500),
  example: z.string().max(2000).optional().nullable(),
  cluster: HookClusterSchema.optional().nullable(),
});

export const UpdateHookSchema = z.object({
  title: z.string().min(1).max(100).optional(),
  template: z.string().min(3).max(500).optional(),
  example: z.string().max(2000).optional().nullable(),
  cluster: HookClusterSchema.optional().nullable(),
  starred: z.union([z.literal(0), z.literal(1)]).optional(),
});

export const ApplyHookSchema = z.object({
  topic: z.string().min(2).max(500),
  platform: PlatformSchema.optional(),
  voice_profile_id: z.string().min(1).max(50).optional().nullable(),
});

export const CreateDraftSchema = z.object({
  title: z.string().max(200).optional().nullable(),
  content: z.string().max(10000).optional(),
  platform: PlatformSchema.optional().nullable(),
  social_account_id: z.string().max(50).optional().nullable(),
  category_id: z.string().max(50).optional().nullable(),
  hook_id: z.string().max(50).optional().nullable(),
  voice_profile_id: z.string().max(50).optional().nullable(),
  metadata_json: z.string().max(5000).optional().nullable(),
});

export const UpdateDraftSchema = z.object({
  title: z.string().max(200).optional().nullable(),
  content: z.string().max(10000).optional(),
  platform: PlatformSchema.optional().nullable(),
  social_account_id: z.string().max(50).optional().nullable(),
  category_id: z.string().max(50).optional().nullable(),
});
